import type { FC } from "react";
import type { OrderDetailDTO } from "../../components/format";
import { SectionHeading } from "../../components/SectionHeading";

/**
 * お届け予定時期 / 配送追跡番号を表示する section。「お届け先 (= 住所)」 とは関心が違うので
 * 独立した section に切り出している (= 顧客視点で「いつ届く / どこまで来た」 を一目で確認)。
 *
 * deliverySchedule (= Logiless 引当からの算出) と trackingNumber (= Shopify Fulfillment 由来)
 * のどちらも無ければ section 自体を出さない。片方だけあるケースもサポート。
 */
export const DeliveryStatusSection: FC<{ order: OrderDetailDTO }> = ({ order }) => {
  const hasSchedule = !!order.deliverySchedule;
  const hasTracking = !!order.trackingNumber;
  if (!hasSchedule && !hasTracking) return null;
  return (
    <section className="mb-10">
      <SectionHeading title="お届け状況" />
      <div className="space-y-1 py-4 text-neutral-700 text-sm leading-relaxed">
        {order.deliverySchedule && (
          <p>
            お届け予定: <span className="text-neutral-900">{order.deliverySchedule.text}</span>
            {order.deliverySchedule.subText && (
              <span className="ml-1 text-neutral-500 text-xs">
                ({order.deliverySchedule.subText})
              </span>
            )}
          </p>
        )}
        {/* 発送保留中は、上のお届け予定が「保留解除後」の目安であることを明示する (誤解防止)。 */}
        {order.deliverySchedule && order.status === "AWAITING_CONFIRMATION" && (
          <p className="text-amber-700 text-xs">
            ※ 上記は発送保留が解除されてからのお届け目安です。
          </p>
        )}
        {order.trackingNumber && (
          <p className="text-neutral-500 text-xs">
            配送追跡番号: <span className="text-neutral-700">{order.trackingNumber}</span>
            {order.trackingUrl && (
              <>
                {" ・ "}
                <a
                  href={order.trackingUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="underline underline-offset-4 hover:text-neutral-900"
                >
                  追跡する
                </a>
              </>
            )}
          </p>
        )}
      </div>
    </section>
  );
};
